import { createStyles, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Checkbox, FormControlLabel, MaterialPalette, TextField } from '@nutrien/cxp-components';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { GroundControlOption } from '@/models/models';
import { useConstructor } from '@/utilities';
import {
  integerGreaterThanZero,
  maxLengthString,
  requiredIntegerGreaterThanZero,
  requiredMaxLengthString,
  requiredNumber,
  requiredString,
} from '@/utilities/validationSchemas';

import GroundControlCheckboxesOption from '../GroundControlCheckboxesOption';
import GroundControlDropdownOption from '../GroundControlDropdownOption';
import GroundControlNumberOption from '../GroundControlNumberOption';
import GroundControlStringOption from '../GroundControlStringOption';
import GroundControlToggleOption from '../GroundControlToggleOption';

export enum GroundControlOptionDisplayType {
  'TOGGLE' = 'Toggle',
  'CHECKBOXES' = 'CheckBoxes',
  'DROPDOWN' = 'Dropdown',
  'NUMBER' = 'Number',
  'STRING' = 'String',
}

enum AllanSpecificFieldsForTestHoles {
  'HOLE_DEPTH' = 'Hole Depth',
  'SEPARATION_DEPTH' = 'Separation Depth',
  'SHEARING_DEPTH' = 'Shearing Depth',
  'TEST_HOLE_TAG' = 'Test Hole Tag #',
}

const validationSchemas = {
  [AllanSpecificFieldsForTestHoles.HOLE_DEPTH.toLocaleLowerCase()]: integerGreaterThanZero(),
  [AllanSpecificFieldsForTestHoles.SEPARATION_DEPTH.toLocaleLowerCase()]: integerGreaterThanZero(),
  [AllanSpecificFieldsForTestHoles.SHEARING_DEPTH.toLocaleLowerCase()]: integerGreaterThanZero(),
  [AllanSpecificFieldsForTestHoles.TEST_HOLE_TAG.toLocaleLowerCase()]: maxLengthString(),
};

const requiredValidationSchemas = {
  [AllanSpecificFieldsForTestHoles.HOLE_DEPTH.toLocaleLowerCase()]:
    requiredIntegerGreaterThanZero(),
  [AllanSpecificFieldsForTestHoles.SEPARATION_DEPTH.toLocaleLowerCase()]:
    requiredIntegerGreaterThanZero(),
  [AllanSpecificFieldsForTestHoles.SHEARING_DEPTH.toLocaleLowerCase()]:
    requiredIntegerGreaterThanZero(),
  [AllanSpecificFieldsForTestHoles.TEST_HOLE_TAG.toLocaleLowerCase()]: requiredMaxLengthString(),
};

const getValidationSchema = (option: GroundControlOption) => {
  const fieldName = option.description.toLocaleLowerCase() as AllanSpecificFieldsForTestHoles;
  const hardcodedSchema = validationSchemas[fieldName];
  if (option.isRequired) {
    return (
      requiredValidationSchemas[fieldName] ||
      hardcodedSchema ||
      (option.displayType === GroundControlOptionDisplayType.NUMBER
        ? requiredNumber()
        : requiredString())
    );
  }
  if (hardcodedSchema) return hardcodedSchema;
  if (!option.isRequired) return undefined;
  return option.displayType === GroundControlOptionDisplayType.NUMBER
    ? requiredNumber()
    : requiredString();
};

interface Props {
  groundControl: any;
  isSelected: boolean;
  selectedValues: any[];
  quantity: string;
  onGroundControlSelected: (
    groundControlId: string,
    quantityValue: string,
    optionValuePairs: any[],
    requiresQuantity: boolean,
  ) => void;
  onGroundControlUpdated: (
    groundControlId: string,
    quantityValue?: string,
    quantityError?: boolean,
    optionId?: string,
    optionValueId?: string,
    optionValue?: string,
    removeOptionValue?: boolean,
    attributeId?: string,
    attributeValueId?: string,
    attributeValue?: string,
  ) => void;
  index: number;
  setValidationError: (index: string | number, value: boolean) => void;
  validationIndex: number;
  setValidationIndex: React.Dispatch<React.SetStateAction<number>>;
  onGroundControlBlur: () => void;
  onGroundControlFocus: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    panel: {
      backgroundColor: MaterialPalette.background.paper2,
      '& h6': {
        fontSize: '20px',
        color: MaterialPalette.tertiary.main,
      },
    },
    checkboxLabel: {
      width: '100%',
    },
    optionsContainer: {
      paddingLeft: '30px',
      '& > div > span': {
        color: MaterialPalette.primary.main,
      },
    },
    option: {
      marginBottom: '15px',
    },
    stepper: {
      width: '45%',
    },
  }),
);

const GroundControlType: FC<Props> = ({
  groundControl,
  isSelected,
  selectedValues,
  quantity,
  onGroundControlSelected,
  onGroundControlUpdated,
  index,
  setValidationError,
  validationIndex,
  setValidationIndex,
  onGroundControlBlur,
  onGroundControlFocus,
}: Props) => {
  const classes = useStyles();

  const [groundControlChecked, setGroundControlChecked] = useState<boolean>(isSelected);
  const [stepperQuantityValue, setStepperQuantityValue] = useState<string>(quantity || '');
  const [quantityErrorText, setQuantityErrorText] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);

  const groundControlOptions = useMemo(() => {
    return JSON.parse(JSON.parse(groundControl.options));
  }, [groundControl.options]);

  const setDefaultOptionValues = () => {
    const result: any[] = [];

    groundControlOptions.forEach(option => {
      if (!option?.id) return;

      if (
        option.displayType === GroundControlOptionDisplayType.NUMBER ||
        option.displayType === GroundControlOptionDisplayType.STRING ||
        !option.values?.length
      ) {
        result.push({ optionId: option.id, value: '' });
      } else {
        const selectedValue = option.values[0];
        result.push({
          optionId: option.id,
          valueId: selectedValue.id,
        });

        selectedValue.attributes?.forEach(attribute => {
          const attributeDefaultValue =
            attribute.values?.find(value => value.isDefault) || attribute.values?.[0];
          if (attributeDefaultValue) {
            result.push({
              optionId: option.id,
              valueId: selectedValue.id,
              attributeId: attribute.id,
              attributeValueId: attributeDefaultValue.id,
            });
          }
        });
      }
    });
    setSelectedOptions(result);
  };

  useConstructor(() => {
    setDefaultOptionValues();
  });

  const collectOptionIds = (options: GroundControlOption[]): string[] => {
    return options.flatMap(option => [
      option.id,
      ...(option.values?.flatMap(value =>
        value.attributes ? collectOptionIds(value.attributes) : [],
      ) || []),
    ]);
  };

  const handleGroundControlChecked = (checked: boolean) => {
    setGroundControlChecked(checked);
    if (checked) {
      setValidationIndex(prev => (prev >= 10 ? prev : 10));
    } else {
      setValidationError(index, false);
      collectOptionIds(groundControlOptions).forEach(optionId => {
        setValidationError(`${index}-${optionId}`, false);
      });
    }
    onGroundControlSelected(
      groundControl.id,
      stepperQuantityValue,
      selectedOptions,
      groundControl.requiresQuantity,
    );
  };
  const validateQuantity = (val: string) => RegExp('^[0-9]+$').test(val) && parseInt(val, 10) > 0;

  useEffect(() => {
    if (groundControl.requiresQuantity && groundControlChecked && validationIndex >= 10) {
      const isValidQuantity = validateQuantity(stepperQuantityValue);

      if (!stepperQuantityValue) {
        setQuantityErrorText('Required');
        setValidationError(index, true);
      } else if (!isValidQuantity) {
        setQuantityErrorText('Invalid quantity');
        setValidationError(index, true);
      } else {
        setQuantityErrorText('');
        setValidationError(index, false);
      }
    } else if (
      groundControl.requiresQuantity &&
      groundControlChecked &&
      stepperQuantityValue === ''
    ) {
      setValidationError(index, true);
    } else {
      setQuantityErrorText('');
      setValidationError(index, false);
    }
  }, [stepperQuantityValue, groundControlChecked, index, validationIndex]);

  const handleQuantityStepperChanged = (val: string) => {
    const isValidQuantity = validateQuantity(val);

    setStepperQuantityValue(val);
    onGroundControlUpdated(groundControl.id, val, !isValidQuantity);
  };

  const handleToggleOptionChanged =
    (option: GroundControlOption, parentOptionId?: string, parentValueId?: string) =>
    (optionId: string, valueId: string) => {
      const quantityInvalid = !validateQuantity(stepperQuantityValue);

      if (parentOptionId && parentValueId) {
        onGroundControlUpdated(
          groundControl.id,
          groundControl.requiresQuantity ? stepperQuantityValue : undefined,
          groundControl.requiresQuantity ? quantityInvalid : false,
          parentOptionId,
          parentValueId,
          undefined,
          false,
          optionId,
          valueId,
        );
        return;
      }

      const allAttributes = option.values?.flatMap(value => value.attributes || []) || [];
      const selectedAttributes =
        option.values?.find(value => value.id === valueId)?.attributes || [];

      allAttributes.forEach(attribute => {
        const defaultValue = selectedAttributes
          .find(selectedAttribute => selectedAttribute.id === attribute.id)
          ?.values?.find(value => value.isDefault === true);

        if (!defaultValue) {
          setValidationError(`${index}-${attribute.id}`, false);
        }

        onGroundControlUpdated(
          groundControl.id,
          groundControl.requiresQuantity ? stepperQuantityValue : undefined,
          groundControl.requiresQuantity ? quantityInvalid : false,
          optionId,
          valueId,
          undefined,
          !defaultValue,
          attribute.id,
          defaultValue?.id,
        );
      });

      onGroundControlUpdated(
        groundControl.id,
        groundControl.requiresQuantity ? stepperQuantityValue : undefined,
        groundControl.requiresQuantity ? quantityInvalid : false,
        optionId,
        valueId,
      );
    };

  const handleDropdownOptionChanged =
    (parentOptionId?: string, parentValueId?: string) => (optionId: string, valueId: string) => {
      const quantityInvalid = !validateQuantity(stepperQuantityValue);

      onGroundControlUpdated(
        groundControl.id,
        groundControl.requiresQuantity ? stepperQuantityValue : undefined,
        groundControl.requiresQuantity ? quantityInvalid : false,
        parentOptionId || optionId,
        parentValueId || valueId,
        undefined,
        false,
        parentOptionId ? optionId : undefined,
        parentOptionId ? valueId : undefined,
      );
    };

  const handleAlphaNumericOptionChanged =
    (parentOptionId?: string, parentValueId?: string) => (optionId: string, value: string) => {
      const quantityInvalid = !validateQuantity(stepperQuantityValue);

      onGroundControlUpdated(
        groundControl.id,
        groundControl.requiresQuantity ? stepperQuantityValue : undefined,
        groundControl.requiresQuantity ? quantityInvalid : false,
        parentOptionId || optionId,
        parentValueId,
        parentOptionId ? undefined : value,
        false,
        parentOptionId ? optionId : undefined,
        undefined,
        parentOptionId ? value : undefined,
      );
    };

  const getSelectedValueOrId = (optionId: string, parentOptionId?: string) => {
    const value = parentOptionId
      ? selectedValues.find(x => x.optionId === parentOptionId && x.attributeId === optionId)
      : selectedValues.find(x => x.optionId === optionId && !x.attributeId);
    return parentOptionId
      ? value?.attributeValueId || value?.attributeValue
      : value?.valueId || value?.value;
  };

  const getAttributes = (option: GroundControlOption) => {
    return option.values?.find(x => x.value === 'Yes')?.attributes || [];
  };

  const hasAttributes = (option: GroundControlOption) => {
    return option.values?.find(x => x.value === 'Yes')?.attributes?.length > 0 || false;
  };

  const isChecked = (option: GroundControlOption) => {
    const valueId = option.values?.find(x => x.value === 'Yes')?.id;
    if (!valueId) return false;
    const selectedValue = selectedValues.find(x => x.optionId === option.id);
    return selectedValue?.valueId === valueId;
  };

  const renderGroundControlOption = (
    option: GroundControlOption,
    gcIndex: number,
    parentOptionId?: string,
    parentValueId?: string,
  ) => {
    if (option.displayType === GroundControlOptionDisplayType.TOGGLE) {
      return (
        <React.Fragment key={`${option.id}-${gcIndex}`}>
          <div>
            <GroundControlToggleOption
              option={option}
              selectedValueId={getSelectedValueOrId(option.id, parentOptionId)}
              optionClassName={classes.option}
              handleValueChanged={handleToggleOptionChanged(option, parentOptionId, parentValueId)}
            />
          </div>
          {hasAttributes(option) && isChecked(option) && (
            <>
              {getAttributes(option).map((attribute, attrIndex) => {
                return renderGroundControlOption(
                  attribute,
                  attrIndex,
                  option.id,
                  getSelectedValueOrId(option.id),
                );
              })}
            </>
          )}
        </React.Fragment>
      );
    }
    if (option.displayType === GroundControlOptionDisplayType.CHECKBOXES) {
      return (
        <div key={`${option.id}-checkboxes`}>
          <GroundControlCheckboxesOption
            key={option.id}
            option={option}
            selectedValueId={getSelectedValueOrId(option.id, parentOptionId)}
            handleValueChanged={handleToggleOptionChanged(option, parentOptionId, parentValueId)}
          />
        </div>
      );
    }
    if (option.displayType === GroundControlOptionDisplayType.DROPDOWN) {
      return (
        <div key={`${option.id}-checkboxes`}>
          <GroundControlDropdownOption
            key={option.id}
            option={option}
            selectedValueId={getSelectedValueOrId(option.id, parentOptionId)}
            handleValueChanged={handleDropdownOptionChanged(parentOptionId, parentValueId)}
          />
        </div>
      );
    }
    if (option.displayType === GroundControlOptionDisplayType.STRING) {
      return (
        <div key={`${option.id}-string`}>
          <GroundControlStringOption
            option={option}
            handleValueChanged={handleAlphaNumericOptionChanged(parentOptionId, parentValueId)}
            currentValue={getSelectedValueOrId(option.id, parentOptionId)}
            setValidationError={setValidationError}
            index={`${index}-${option.id}`}
            validationSchema={getValidationSchema(option)}
            forceValidate={validationIndex >= 10}
          />
        </div>
      );
    }
    if (option.displayType === GroundControlOptionDisplayType.NUMBER) {
      return (
        <div key={`${option.id}-number`}>
          <GroundControlNumberOption
            option={option}
            handleValueChanged={handleAlphaNumericOptionChanged(parentOptionId, parentValueId)}
            currentValue={getSelectedValueOrId(option.id, parentOptionId)}
            setValidationError={setValidationError}
            index={`${index}-${option.id}`}
            validationSchema={getValidationSchema(option)}
            forceValidate={validationIndex >= 10}
          />
        </div>
      );
    }
    return <div key={`${groundControl.id}-default`} />;
  };

  return (
    <div>
      <Grid item xs={12} key={groundControl.id}>
        <FormControlLabel
          control={
            <Checkbox
              key={`${groundControl.id}-main-checkbox`}
              onChange={(evt: any) => {
                handleGroundControlChecked(evt.target.checked);
              }}
              name="remediation-complete-checkbox"
              checked={groundControlChecked}
            />
          }
          label={groundControl.description}
          className={classes.checkboxLabel}
        />
      </Grid>
      {groundControlChecked && (
        <>
          {groundControl.requiresQuantity && (
            <Grid item xs={12} className={classes.optionsContainer}>
              <TextField
                key={`${groundControl.id}-quantity-checkbox`}
                label="Quantity"
                required
                onChange={event => handleQuantityStepperChanged(event.target.value)}
                onBlur={onGroundControlBlur}
                onFocus={onGroundControlFocus}
                value={stepperQuantityValue}
                variant="outlined"
                data-cy="quantity-checkbox"
                inputProps={{ inputMode: 'numeric' }}
                error={quantityErrorText !== ''}
                errorText={quantityErrorText}
              />
            </Grid>
          )}
          {groundControlOptions.map((option, gcIndex) => {
            return renderGroundControlOption(option, gcIndex);
          })}
        </>
      )}
    </div>
  );
};

export default GroundControlType;
